import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FavoriteHeart } from '../components/FavoriteHeart.js';
import { MapCard } from '../components/MapCard.js';
import { RejectModal } from '../components/RejectModal.js';
import { ScoreBadge } from '../components/ScoreBadge.js';
import { ShareModal } from '../components/ShareModal.js';
import {
  getObject,
  objectPhase,
  type ObjectEvent,
  type ObjectStatus,
  type ObjectView,
  profileLabel,
  rejectObject,
  type RejectCategory,
  setNote,
  shortlistObject,
  updateStatus,
} from '../lib/api.js';

const STATUS_OPTIONS: ObjectStatus[] = ['new', 'partner_review', 'match', 'contacted', 'rejected'];

const STATUS_LABEL: Record<ObjectStatus, string> = {
  new: 'Neu (Swipe)',
  partner_review: 'Partner-Check',
  match: '🎯 Match',
  contacted: '✓ In Kontakt',
  rejected: 'Abgelehnt',
};

function fmtPrice(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtArea(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} m²`;
}

function fmtPerSqm(price: number | null | undefined, area: number | null | undefined): string {
  if (price === null || price === undefined || area === null || area === undefined || area === 0) {
    return '—';
  }
  return `${(price / area).toFixed(1)} €/m²`;
}

export function ObjectDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const idNum = Number(id);
  const qc = useQueryClient();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectCategory, setRejectCategory] = useState<RejectCategory | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [shareModal, setShareModal] = useState<
    { text: string; title: string; url?: string } | null
  >(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['object', idNum],
    queryFn: () => getObject(idNum),
    enabled: Number.isInteger(idNum),
  });

  const statusMutation = useMutation({
    mutationFn: (status: ObjectStatus) => updateStatus(idNum, status),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['object', idNum] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      notifications.show({
        message: `Status auf "${STATUS_LABEL[res.status]}" gesetzt`,
        color: 'green',
      });
    },
    onError: (err) => {
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ category, note }: { category: RejectCategory; note: string }) =>
      rejectObject(idNum, category, note || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['object', idNum] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      setRejectOpen(false);
      setRejectCategory(null);
      setRejectNote('');
      notifications.show({ message: '✗ Abgelehnt', color: 'red' });
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const shortlistMutation = useMutation({
    mutationFn: () => shortlistObject(idNum),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['object', idNum] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      setShareModal({
        text: res.whatsappText,
        title: data?.listing?.title ?? 'Inserat',
        url: data?.listing?.url ?? undefined,
      });
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  /** rejected → Modal, partner_review → shortlist-API (+ WhatsApp-Share), sonst direkt PATCH. */
  const handleStatusSet = (s: ObjectStatus): void => {
    if (s === 'rejected') {
      setRejectOpen(true);
      return;
    }
    if (s === 'partner_review') {
      shortlistMutation.mutate();
      return;
    }
    statusMutation.mutate(s);
  };

  const submitReject = (): void => {
    if (!rejectCategory) return;
    rejectMutation.mutate({ category: rejectCategory, note: rejectNote });
  };

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }
  if (error) return <Text c="red">Fehler: {String(error)}</Text>;
  if (!data) return <Text>Objekt nicht gefunden.</Text>;

  return (
    <Stack gap="lg">
      <Anchor component={Link} to="/" size="sm" c="dimmed">
        ← Zurück zur Liste
      </Anchor>

      <PhaseStepper data={data} />
      <Hero data={data} />
      <ActionButtons
        status={data.status}
        onSet={handleStatusSet}
        pending={
          statusMutation.isPending || rejectMutation.isPending || shortlistMutation.isPending
        }
      />
      <KeyFacts data={data} />
      <DigestNote data={data} />
      <ImageMapRow data={data} />
      <CommuteSection data={data} />
      <BreakdownSection data={data} />
      <NotesSection key={data.id} data={data} />
      <ListingSection data={data} />

      <RejectModal
        opened={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          setRejectCategory(null);
          setRejectNote('');
        }}
        note={rejectNote}
        onNoteChange={setRejectNote}
        onPick={(category) => rejectMutation.mutate({ category, note: rejectNote })}
        pending={rejectMutation.isPending}
      />

      <ShareModal
        opened={shareModal !== null}
        onClose={() => setShareModal(null)}
        text={shareModal?.text ?? ''}
        title={shareModal?.title ?? ''}
        listingUrl={shareModal?.url}
      />
    </Stack>
  );
}

function Hero({ data }: { data: ObjectView }): JSX.Element {
  const killed = (data.score?.killedBy ?? null) !== null;
  const profileBadgeColor = data.assignedProfile === 'B_buero' ? 'cyan' : 'violet';
  const phase = objectPhase(data);
  const listingUrl = data.listing?.url ?? null;

  return (
    <Card padding="lg">
      <Group align="flex-start" wrap="wrap" gap="md">
        {/* Score + Titel klickbar → Inserat */}
        {listingUrl ? (
          <a href={listingUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', lineHeight: 0 }}>
            <ScoreBadge score={data.score?.effectiveTotal} size="lg" killed={killed} />
          </a>
        ) : (
          <ScoreBadge score={data.score?.effectiveTotal} size="lg" killed={killed} />
        )}

        <Stack gap={8} style={{ flex: 1, minWidth: 220 }}>
          <Group gap={6} wrap="nowrap" justify="space-between" align="center">
            <Group gap={6} wrap="wrap">
              <Badge size="md" color={profileBadgeColor} variant="light" tt="none">
                {profileLabel(data.assignedProfile)}
              </Badge>
              <Badge size="md" variant="light" color={phase.color} tt="none">
                {phase.label}
              </Badge>
            </Group>
            <FavoriteHeart objectId={data.id} favorited={data.favorited} size={24} />
          </Group>
          {listingUrl ? (
            <Anchor
              href={listingUrl}
              target="_blank"
              rel="noreferrer"
              underline="never"
              c="inherit"
            >
              <Title
                order={2}
                style={{ letterSpacing: '-0.02em', wordBreak: 'break-word', hyphens: 'auto' }}
              >
                {data.listing?.title ?? '—'}
              </Title>
            </Anchor>
          ) : (
            <Title
              order={2}
              style={{ letterSpacing: '-0.02em', wordBreak: 'break-word', hyphens: 'auto' }}
            >
              {data.listing?.title ?? '—'}
            </Title>
          )}
          <Text c="dimmed" size="sm" style={{ wordBreak: 'break-word' }}>
            {data.address ?? '—'}
          </Text>
          {killed && data.score?.killedBy && (
            <Group gap={6} mt={4} wrap="wrap">
              <Badge color="red" variant="light" tt="none">
                ✕ Killcriterion
              </Badge>
              <Text
                size="xs"
                c="red.7"
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  wordBreak: 'break-word',
                }}
              >
                {data.score.killedBy}
              </Text>
            </Group>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

/** Hinweis, wenn die KI gelaufen ist, aber der Digest keine Detailangaben hergab. */
function DigestNote({ data }: { data: ObjectView }): JSX.Element | null {
  const DETAIL_FIELDS = [
    'zustand',
    'energieklasse',
    'befristung',
    'baujahr',
    'außenbereich',
    'stockwerk',
    'lift_vorhanden',
    'ablöse_euro',
  ];
  const hasDetail = DETAIL_FIELDS.some((f) => {
    const v = data.extracted?.[f];
    return v != null && v !== 'null';
  });
  if (!(data.score?.isReif ?? false) || hasDetail) return null;
  return (
    <Text size="sm" c="dimmed" fs="italic">
      ℹ Nur Eckdaten — im Inserat stehen keine KI-auswertbaren Detailangaben (bei immoscout-
      Kurzinseraten normal). Bewertet wird auf Preis, Größe, Zimmer, Lage & Fahrzeit.
    </Text>
  );
}

type Tone = 'good' | 'bad' | 'neutral';
function scoreTone(score: number | null): Tone {
  if (score === null) return 'neutral';
  if (score >= 0.7) return 'good';
  if (score <= 0.4) return 'bad';
  return 'neutral';
}

function KeyFacts({ data }: { data: ObjectView }): JSX.Element {
  const s = data.latestSnapshot;
  const isKauf = data.dealType === 'kauf' || data.assignedProfile === 'kauf';
  const bd = data.score?.breakdown ?? [];
  const scoreOf = (key: string): number | null => {
    const e = bd.find((b) => b.key === key);
    if (!e || e.applicable === false) return null;
    return e.score;
  };
  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
      <FactCard label="Fläche" value={fmtArea(s?.areaSqm)} tone={scoreTone(scoreOf('wohnfläche'))} />
      <FactCard
        label={isKauf ? 'Kaufpreis' : 'Bruttomiete'}
        value={fmtPrice(s?.price)}
        subtitle={fmtPerSqm(s?.price, s?.areaSqm)}
        tone={scoreTone(scoreOf(isKauf ? 'kaufpreis' : 'preis'))}
      />
      <FactCard
        label="Zimmer"
        value={s?.rooms !== null && s?.rooms !== undefined ? String(s.rooms) : '—'}
        tone={scoreTone(scoreOf('zimmer'))}
      />
      <FactCard
        label="Ø Fahrzeit"
        value={data.avgCommuteMin !== null ? `${data.avgCommuteMin} min` : '—'}
        tone={scoreTone(
          (() => {
            const pa = scoreOf('pendel_arbeit');
            const pe = scoreOf('pendel_eltern');
            return pa !== null && pe !== null ? (2 * pa + pe) / 3 : (pa ?? pe);
          })(),
        )}
      />
    </SimpleGrid>
  );
}

function FactCard({
  label,
  value,
  subtitle,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  subtitle?: string;
  tone?: Tone;
}): JSX.Element {
  const bg =
    tone === 'good' ? '#e7f9ee' : tone === 'bad' ? '#fdecec' : undefined;
  const border =
    tone === 'good' ? '#86efac' : tone === 'bad' ? '#fca5a5' : undefined;
  return (
    <Card padding="md" style={bg ? { background: bg, borderColor: border } : undefined} withBorder={!!bg}>
      <Stack gap={2}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.05em' }}>
          {label}
        </Text>
        <Text fw={700} size="xl" style={{ letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {subtitle}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/** Foto (falls vorhanden) links neben der Karte; Klick aufs Foto → Inserat. Ohne Foto: Karte vollflächig. */
function ImageMapRow({ data }: { data: ObjectView }): JSX.Element {
  const imageUrl = data.listing?.imageUrl ?? null;
  const listingUrl = data.listing?.url ?? null;
  if (!imageUrl) return <MapCard object={data} />;
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" style={{ alignItems: 'stretch' }}>
      <a
        href={listingUrl ?? undefined}
        target="_blank"
        rel="noreferrer"
        style={{ display: 'block', height: '100%', lineHeight: 0, borderRadius: 12, overflow: 'hidden' }}
      >
        <img
          src={imageUrl}
          alt={data.listing?.title ?? 'Inserat'}
          style={{ width: '100%', height: '100%', minHeight: 260, objectFit: 'cover', display: 'block' }}
        />
      </a>
      <MapCard object={data} />
    </SimpleGrid>
  );
}

function CommuteSection({ data }: { data: ObjectView }): JSX.Element {
  const max = 60;
  return (
    <Card padding="lg">
      <Stack gap="lg">
        <Title order={4}>Pendelzeiten</Title>

        {data.commutes.map((c) => (
          <Stack key={c.target} gap="xs">
            <Group justify="space-between" gap="xs" wrap="wrap" align="baseline">
              <Stack gap={0}>
                <Text fw={600} size="sm">
                  {c.displayName}
                  {c.role === 'secondary' && (
                    <Text component="span" size="xs" c="dimmed" ml={6}>
                      (sekundär)
                    </Text>
                  )}
                </Text>
                <Text size="xs" c="dimmed">
                  {c.address}
                </Text>
              </Stack>
            </Group>
            <Stack gap="xs">
              <CommuteRow
                icon="🚗"
                label="Auto"
                minutes={c.driving?.minutes ?? null}
                km={c.driving?.distanceKm ?? null}
                max={max}
                isBest={c.bestMinutes !== null && c.driving?.minutes === c.bestMinutes}
              />
              <CommuteRow
                icon="🚆"
                label="ÖV"
                minutes={c.transit?.minutes ?? null}
                km={c.transit?.distanceKm ?? null}
                max={max}
                isBest={c.bestMinutes !== null && c.transit?.minutes === c.bestMinutes}
              />
            </Stack>
          </Stack>
        ))}

        {data.commutes.length === 0 && (
          <Text c="dimmed" size="sm">
            Keine Pendelzeit-Daten verfügbar.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function CommuteRow({
  icon,
  label,
  minutes,
  km,
  max,
  isBest,
}: {
  icon: string;
  label: string;
  minutes: number | null;
  km: number | null;
  max: number;
  isBest: boolean;
}): JSX.Element {
  // Bar-Semantik: voll = bestmöglich (0 min), leer = worst case (>= max).
  // Score-Anteil: (max - minutes) / max, geclampt auf [0, 100].
  const fraction =
    minutes === null ? 0 : Math.max(0, Math.min(1, (max - minutes) / max));
  const pct = fraction * 100;
  const barColor = minutes === null ? 'gray' : barColorForScore(fraction);
  return (
    <Stack gap={6}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Group gap={6} wrap="nowrap">
          <Text style={{ fontSize: '1.125rem' }}>{icon}</Text>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {isBest && (
            <Badge size="xs" color="teal" variant="light" tt="none">
              best
            </Badge>
          )}
        </Group>
        <Group gap={6} wrap="nowrap">
          <Text fw={600} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {minutes !== null ? `${Math.round(minutes)} min` : '—'}
          </Text>
          {km !== null && (
            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
              · {km.toFixed(1)} km
            </Text>
          )}
        </Group>
      </Group>
      <Progress value={pct} color={barColor} size="md" radius="sm" />
    </Stack>
  );
}

/**
 * Einheitliche Farbsemantik für 0..1 Score-Anzeigen.
 * Voller Balken (1.0) → teal. Leer (0) → red.
 */
function barColorForScore(score: number): string {
  if (score >= 0.75) return 'teal';
  if (score >= 0.5) return 'lime';
  if (score >= 0.25) return 'yellow';
  if (score > 0) return 'orange';
  return 'red';
}

function BreakdownSection({ data }: { data: ObjectView }): JSX.Element {
  if (!data.score) {
    return (
      <Card padding="lg">
        <Title order={4}>Score-Aufschlüsselung</Title>
        <Text c="dimmed" mt="sm">
          Noch nicht gescort.
        </Text>
      </Card>
    );
  }
  if (data.score.killedBy) {
    return (
      <Card padding="lg">
        <Title order={4}>Score-Aufschlüsselung</Title>
        <Text c="dimmed" mt="sm" size="sm">
          Objekt wurde durch ein Killcriterion ausgeschlossen — keine Detail-Aufschlüsselung.
        </Text>
      </Card>
    );
  }
  if (!data.score.breakdown || data.score.breakdown.length === 0) {
    return (
      <Card padding="lg">
        <Title order={4}>Score-Aufschlüsselung</Title>
        <Text c="dimmed" mt="sm">
          Kein Breakdown gespeichert.
        </Text>
      </Card>
    );
  }

  // Nach Phase gruppieren
  const ruleEntries = data.score.breakdown.filter((c) => (c.phase ?? 'rule') === 'rule');
  const llmEntries = data.score.breakdown.filter((c) => c.phase === 'llm');
  const ruleSum = ruleEntries.reduce((s, c) => s + c.contribution, 0);
  const llmSum = llmEntries.reduce((s, c) => s + c.contribution, 0);
  const ruleMaxWeight = ruleEntries.reduce((s, c) => s + (c.weight ?? 0), 0);
  const llmMaxWeight = llmEntries.reduce((s, c) => s + (c.weight ?? 0), 0);

  return (
    <Card padding="lg">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={4}>Score-Aufschlüsselung</Title>
        <Stack gap={2} align="flex-end">
          <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
            Effective: <strong>{data.score.effectiveTotal.toFixed(3)}</strong>
            {data.score.decayDays >= 0.5 && data.score.total !== data.score.effectiveTotal && (
              <>
                {' '}<span style={{ opacity: 0.6 }}>(raw {data.score.total.toFixed(3)})</span>
              </>
            )}
          </Text>
          {data.score.decayDays >= 0.5 && (
            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
              −{data.score.decayDays.toFixed(1)}d Decay · 0.05/Tag ab firstSeenAt
            </Text>
          )}
        </Stack>
      </Group>

      {ruleEntries.length > 0 && (
        <Stack gap={8} mb="lg">
          <Group justify="space-between" align="baseline">
            <Text size="sm" fw={600} c="violet.7">
              Phase 1 — regelbasiert
            </Text>
            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
              Σ {ruleSum.toFixed(3)} / max {ruleMaxWeight.toFixed(2)}
            </Text>
          </Group>
          {ruleEntries
            .sort((a, b) => b.contribution - a.contribution)
            .map((c) => (
              <BreakdownRow key={c.key} entry={c} />
            ))}
        </Stack>
      )}

      {llmEntries.length > 0 && (
        <Stack gap={8}>
          <Group justify="space-between" align="baseline">
            <Text size="sm" fw={600} c="teal.7">
              Phase 2 — LLM-Anreicherung
            </Text>
            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
              Σ {llmSum.toFixed(3)} / max {llmMaxWeight.toFixed(2)}
            </Text>
          </Group>
          {llmEntries
            .sort((a, b) => b.contribution - a.contribution)
            .map((c) => (
              <BreakdownRow key={c.key} entry={c} />
            ))}
        </Stack>
      )}
    </Card>
  );
}

const CRIT_LABELS: Record<string, string> = {
  kaufpreis: 'Kaufpreis',
  preis: 'Preis (Miete)',
  preis_pro_m2: '€ / m²',
  wohnfläche: 'Wohnfläche',
  zimmer: 'Zimmer',
  pendel_arbeit: 'Fahrzeit Hauptziel',
  pendel_eltern: 'Fahrzeit weitere Ziele',
  lage: 'Lage',
  oev_naehe: 'ÖV-Nähe',
  außenbereich: 'Außenbereich',
  baujahr: 'Baujahr',
  energieklasse: 'Energieklasse',
  befristung: 'Befristung',
  ablöse: 'Ablöse',
};
function critLabel(key: string): string {
  return CRIT_LABELS[key] ?? key;
}

function BreakdownRow({
  entry,
}: {
  entry: {
    key: string;
    score: number;
    contribution: number;
    value: number | string | null;
    weight?: number;
    applicable?: boolean;
  };
}): JSX.Element {
  const valueDisplay =
    entry.value === null || entry.value === undefined
      ? null
      : typeof entry.value === 'number'
        ? entry.value.toFixed(2)
        : entry.value;

  // Nicht-anwendbar = unbekanntes LLM-Feld → floss NICHT in den Score ein; sein
  // Gewicht wurde auf die bekannten Kriterien umverteilt. Gedimmt, ohne Balken/Beitrag.
  if (entry.applicable === false) {
    return (
      <Stack gap={4} style={{ opacity: 0.45 }}>
        <Group justify="space-between" gap="xs" wrap="nowrap" align="baseline">
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
              {critLabel(entry.key)}
            </Text>
            <Text size="xs" c="dimmed">
              {valueDisplay ?? 'unbekannt'}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" fs="italic" style={{ whiteSpace: 'nowrap' }}>
            keine Info · fließt nicht ein
          </Text>
        </Group>
        <Progress value={0} color="gray" size="sm" radius="sm" />
      </Stack>
    );
  }

  // Bar-Semantik: voller Balken = 1.0 (Kategorie maximal erreicht), leer = 0.
  // entry.score liegt immer in [0,1] (engine clampt).
  const pct = Math.max(0, Math.min(100, entry.score * 100));
  const barColor = barColorForScore(entry.score);
  return (
    <Stack gap={4}>
      <Group justify="space-between" gap="xs" wrap="nowrap" align="baseline">
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
            {critLabel(entry.key)}
          </Text>
          {valueDisplay && (
            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {valueDisplay}
            </Text>
          )}
        </Group>
        <Group gap={6} wrap="nowrap">
          <Text
            size="xs"
            c="dimmed"
            style={{ fontVariantNumeric: 'tabular-nums' }}
            title="Kategorie-Score (0..1 = leer..voll)"
          >
            {entry.score.toFixed(2)}
          </Text>
          <Text
            fw={600}
            size="sm"
            style={{ fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}
            title={`Beitrag = Score × Gewicht (${(entry.weight ?? 0).toFixed(2)})`}
          >
            +{entry.contribution.toFixed(3)}
          </Text>
        </Group>
      </Group>
      <Progress value={pct} color={barColor} size="sm" radius="sm" />
    </Stack>
  );
}

function fmtRel(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86400000);
  if (days <= 0) return 'heute';
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} T`;
  if (days < 30) return `vor ${Math.floor(days / 7)} Wo`;
  return new Date(ts).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function eventDisplay(e: ObjectEvent): { icon: string; text: string; detail?: string } {
  const by = e.payload?.['by'];
  const who = by === 'partner' ? 'Partner' : by === 'user' ? 'dir' : e.source;
  switch (e.eventType) {
    case 'rejected': {
      const cat = e.payload?.['category'];
      const note = e.payload?.['note'];
      return {
        icon: '✗',
        text: `Abgelehnt von ${who}${typeof cat === 'string' ? ` · ${cat}` : ''}`,
        detail: typeof note === 'string' && note.length > 0 ? note : undefined,
      };
    }
    case 'partner_review':
      return { icon: '👍', text: 'Hot geswiped' };
    case 'partner_approved':
      return { icon: '👍', text: 'Partner ist auch dafür' };
    case 'contacted':
      return { icon: '☎', text: 'Kontakt aufgenommen (In Kontakt)' };
    case 'unswipe':
      return { icon: '↶', text: 'Swipe rückgängig gemacht' };
    case 'status_change': {
      const to = e.payload?.['to'];
      return { icon: '↻', text: `Status geändert → ${typeof to === 'string' ? to : '?'}` };
    }
    default:
      return { icon: '•', text: e.eventType };
  }
}

function NotesSection({ data }: { data: ObjectView }): JSX.Element {
  const qc = useQueryClient();
  const [text, setText] = useState(data.note ?? '');

  const noteMut = useMutation({
    mutationFn: (t: string) => setNote(data.id, t),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['object', data.id] });
      notifications.show({ message: '📝 Notiz gespeichert', color: 'green' });
    },
    onError: (err) => notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const events = data.events ?? [];
  const dirty = text.trim() !== (data.note ?? '').trim();

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Title order={4}>Notiz & Verlauf</Title>

        <Textarea
          placeholder='Eigene Notiz … z.B. „Makler Montag anrufen", „Nähe guter Bäckerei", „Bad altmodisch"'
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
        />
        <Group justify="flex-end">
          <Button
            size="xs"
            onClick={() => noteMut.mutate(text)}
            loading={noteMut.isPending}
            disabled={!dirty}
          >
            Notiz speichern
          </Button>
        </Group>

        {events.length > 0 && (
          <Stack gap={10}>
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: '0.05em' }}
            >
              Verlauf
            </Text>
            {events.map((e, i) => {
              const d = eventDisplay(e);
              return (
                <Group key={i} gap={10} wrap="nowrap" align="flex-start">
                  <Text style={{ fontSize: '1rem', lineHeight: 1.3 }}>{d.icon}</Text>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm">{d.text}</Text>
                    {d.detail && (
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{ fontStyle: 'italic', wordBreak: 'break-word' }}
                      >
                        „{d.detail}"
                      </Text>
                    )}
                  </Stack>
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {fmtRel(e.occurredAt)}
                  </Text>
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

const PHASE_STEPS = [
  { num: 1, label: 'Autoscoring' },
  { num: 2, label: 'KI-Analyse' },
  { num: 3, label: 'Swipen' },
  { num: 4, label: 'Partner' },
  { num: 5, label: 'Match' },
  { num: 6, label: 'In Kontakt' },
];

/** Oben: „Weiterbewerten" + Phasen-Fortschritt 1→6 (bzw. „Abgelehnt"). */
function PhaseStepper({ data }: { data: ObjectView }): JSX.Element {
  const phase = objectPhase(data);
  const isRejected = data.status === 'rejected';
  return (
    <Card padding="md">
      <Stack gap={8}>
        <Text size="sm" fw={700}>
          Weiterbewerten
        </Text>
        {isRejected ? (
          <Badge color="gray" variant="light" size="lg" tt="none">
            ✗ Abgelehnt
          </Badge>
        ) : (
          <Group gap={4} wrap="nowrap" style={{ overflowX: 'auto' }}>
            {PHASE_STEPS.map((p) => {
              const done = phase.num > p.num;
              const current = phase.num === p.num;
              return (
                <Group key={p.num} gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Box
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      background: current ? '#7c3aed' : done ? '#c4b5fd' : '#e2e8f0',
                      color: current ? '#fff' : done ? '#4c1d95' : '#94a3b8',
                    }}
                  >
                    {done ? '✓' : p.num}
                  </Box>
                  {/* Am Handy nur das aktuelle Label zeigen (sonst passt nicht alles nebeneinander),
                      auf Desktop alle. */}
                  <Text
                    size="xs"
                    c={current ? 'violet.7' : 'dimmed'}
                    fw={current ? 700 : 400}
                    style={{ whiteSpace: 'nowrap' }}
                    visibleFrom={current ? undefined : 'sm'}
                  >
                    {p.label}
                  </Text>
                  {p.num < PHASE_STEPS.length && (
                    <Text c="dimmed" size="xs">
                      ›
                    </Text>
                  )}
                </Group>
              );
            })}
          </Group>
        )}
      </Stack>
    </Card>
  );
}

/** Nach dem Hero: prominente kontextuelle Aktions-Buttons. */
function ActionButtons({
  status,
  onSet,
  pending,
}: {
  status: ObjectStatus;
  onSet: (s: ObjectStatus) => void;
  pending: boolean;
}): JSX.Element {
  const actions: Array<{ label: string; status: ObjectStatus; color: string }> = [];
  if (status === 'rejected') {
    actions.push({ label: '↩ Reaktivieren', status: 'new', color: 'green' });
  } else {
    if (status === 'new') actions.push({ label: '★ An Partner', status: 'partner_review', color: 'pink' });
    // Match → Kontakt aufgenommen (Terminal). Manueller Override auch aus Partner-Check möglich.
    if (status === 'match') actions.push({ label: '☎ Kontakt aufgenommen', status: 'contacted', color: 'teal' });
    if (status === 'partner_review') actions.push({ label: '🎯 Als Match markieren', status: 'match', color: 'yellow' });
    if (status !== 'new') actions.push({ label: '↩ Zurück zu „Neu"', status: 'new', color: 'gray' });
    actions.push({ label: '✗ Ablehnen', status: 'rejected', color: 'red' });
  }
  return (
    <Group gap="sm" wrap="wrap" grow>
      {actions.map((a) => (
        <Button
          key={a.status + a.label}
          variant="filled"
          color={a.color}
          onClick={() => onSet(a.status)}
          disabled={pending}
          size="md"
          style={{ minWidth: 130 }}
        >
          {a.label}
        </Button>
      ))}
    </Group>
  );
}

function ListingSection({ data }: { data: ObjectView }): JSX.Element | null {
  if (!data.listing) return null;
  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Inserat</Title>
          <Badge variant="light" tt="none">
            {data.listing.portal}
          </Badge>
        </Group>
        <Anchor href={data.listing.url} target="_blank" rel="noreferrer" size="sm">
          {data.listing.url}
        </Anchor>
        <Group gap="md">
          <Text size="xs" c="dimmed">
            seit {new Date(data.listing.firstSeenAt).toLocaleDateString('de-AT')}
          </Text>
          {data.listing.lastSeenAt !== data.listing.firstSeenAt && (
            <Text size="xs" c="dimmed">
              · zuletzt: {new Date(data.listing.lastSeenAt).toLocaleDateString('de-AT')}
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
